import { useMemo, useState } from "react";

import AlertComponent from "../../../helpers/alert/AlertComponent";
import { DocumentViewerModal } from "../../../features/beneficiary-document-reports/ui/DocumentViewerModal";
import { ManagementMetaStrip } from "../../../shared/ui/management-meta-strip";
import { SmartTable } from "../../../shared/ui/smart-table";
import { buildManagementMetaItems } from "../model/buildManagementMetaItems";
import { buildAlertsToManageColumns } from "../model/columns/buildAlertsToManageColumns";
import { buildAssociatedProductColumns } from "../model/columns/buildAssociatedProductColumns";
import { triggerDownload } from "../lib/fileDownload";
import { useActaFile } from "../model/useActaFile";
import { useIndeterminateManagement } from "../model/useIndeterminateManagement";
import { useUnitOptions } from "../model/useUnitOptions";
import { usePdfViewer } from "../model/usePdfViewer";
import { IndeterminateProductFields } from "./IndeterminateProductFields";
import { JourneyDocumentsSection } from "./JourneyDocumentsSection";
import { SolicitudForm } from "./SolicitudForm";
import {
  ActionsRow,
  ManagementCard,
  PrimaryActionButton,
  SecondaryActionButton,
  SectionCard,
  SectionTitle,
} from "./common.styles";
import {
  EmptyState,
  ResponsiveBody,
  SectionHeaderRow,
  StepPill,
  TableWrapper,
} from "./IndeterminateManagementView.styles";

export const IndeterminateManagementView = ({
  assignment,
  historyByCategory = { pdf: [], excel: [] },
  managementTypeOptions = [],
  onBack,
  onContinue,
  onSubmitManagementRequest,
}) => {
  const row = assignment?.selectedRows?.[0] ?? null;
  const managementTypeLabel = assignment?.managementType || "INDETERMINADO";

  const [submitting, setSubmitting] = useState(false);

  const {
    observation,
    setObservation,
    form,
    setField,
    canSubmit,
    managementTypeId,
    buildSelectedRows,
  } = useIndeterminateManagement({ row, assignment, managementTypeOptions });

  const { unitOptions, unitsLoading } = useUnitOptions();
  const pdf = usePdfViewer();
  const acta = useActaFile({ isPriceAdjustment: false, managementTypeLabel });

  const handleDownloadActa = () => {
    if (!acta.actaFile) return;
    const url = URL.createObjectURL(acta.actaFile);
    triggerDownload(url, acta.actaFile.name);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const canSend = canSubmit && Boolean(acta.actaFile);

  const metaItems = useMemo(
    () => buildManagementMetaItems(managementTypeLabel, { isIndeterminate: true }),
    [managementTypeLabel]
  );

  const associatedColumns = useMemo(() => buildAssociatedProductColumns(), []);
  const alertsColumns = useMemo(() => buildAlertsToManageColumns(), []);

  const activePdf = historyByCategory.pdf?.[0] ?? null;
  const activeExcel = historyByCategory.excel?.[0] ?? null;

  // Consume los dos recursos del envío (igual que "Ajuste de precio"): actualizar
  // la gestión y, solo si esa respondió OK, crear la solicitud documental.
  const handleSubmit = async () => {
    if (!canSend || submitting) return;

    if (!onSubmitManagementRequest) {
      onContinue?.();
      return;
    }

    if (managementTypeId == null) {
      AlertComponent.warning(
        "Información incompleta",
        "No fue posible identificar el tipo de gestión de la solicitud actual."
      );
      return;
    }

    setSubmitting(true);
    try {
      const result = await onSubmitManagementRequest({
        managementTypeId,
        selectedRows: buildSelectedRows(),
        observation: observation.trim(),
        pdf: acta.actaFile,
        isHomologation: false,
      });

      if (result?.success) {
        AlertComponent.success(
          "Envío exitoso",
          result?.request?.message || "Solicitud creada correctamente."
        );
        onContinue?.();
      } else {
        AlertComponent.error(
          "No fue posible completar el envío",
          result?.request?.message ||
            result?.management?.message ||
            "Ocurrió un error al procesar la solicitud."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ManagementCard bordered={false}>
      <ResponsiveBody>
        <ManagementMetaStrip items={metaItems} />

        <JourneyDocumentsSection
          activePdf={activePdf}
          activeExcel={activeExcel}
          viewingPdf={pdf.loadingRoute}
          onViewPdf={() => pdf.openFromRoute(activePdf?.route, activePdf?.name)}
        />

        <SolicitudForm
          isPriceAdjustment={false}
          documentLabel={managementTypeLabel}
          observation={observation}
          onObservationChange={setObservation}
          actaFile={acta.actaFile}
          onBeforeUploadActa={acta.beforeUpload}
          onViewActa={() => pdf.openFromFile(acta.actaFile)}
          onDownloadActa={handleDownloadActa}
          onRemoveActa={acta.clear}
        />

        {row ? (
          <>
            <SectionCard>
              <SectionHeaderRow>
                <SectionTitle>Nuevo producto para el catálogo de la jornada</SectionTitle>
                <StepPill>Paso 1 · Implementación</StepPill>
              </SectionHeaderRow>
              <IndeterminateProductFields
                row={row}
                form={form}
                onFieldChange={setField}
                unitOptions={unitOptions}
                unitsLoading={unitsLoading}
              />
            </SectionCard>

            <SectionCard>
              <SectionTitle>Producto alertado asociado a la solicitud</SectionTitle>
              <TableWrapper>
                <SmartTable
                  rowKey="id"
                  columns={associatedColumns}
                  columnWidthMode="fixed"
                  dataSource={[row]}
                  showPagination={false}
                  showToolbar={false}
                  enableRowSelection={false}
                  showColumnSettings={false}
                  showTableResize={false}
                  showReload={false}
                  scroll={{ x: 1600 }}
                  emptyText="Sin producto asociado."
                />
              </TableWrapper>
            </SectionCard>

            <SectionCard>
              <SectionTitle>Alertas a Gestionar</SectionTitle>
              <TableWrapper>
                <SmartTable
                  rowKey="id"
                  columns={alertsColumns}
                  columnWidthMode="fixed"
                  dataSource={[row]}
                  showPagination={false}
                  showToolbar={false}
                  enableRowSelection={false}
                  showColumnSettings={false}
                  showTableResize={false}
                  showReload={false}
                  scroll={{ x: 1760 }}
                  emptyText="Sin alertas para gestionar."
                />
              </TableWrapper>
            </SectionCard>
          </>
        ) : (
          <SectionCard>
            <EmptyState>
              No hay un producto seleccionado para gestionar. Vuelve y selecciona un
              único ítem para el tipo de gestión INDETERMINADO.
            </EmptyState>
          </SectionCard>
        )}

        <ActionsRow>
          <SecondaryActionButton onClick={onBack}>Cancelar</SecondaryActionButton>
          <PrimaryActionButton
            type="primary"
            disabled={!canSend}
            loading={submitting}
            onClick={handleSubmit}
          >
            Enviar
          </PrimaryActionButton>
        </ActionsRow>
      </ResponsiveBody>

      <DocumentViewerModal
        isOpen={pdf.viewer.isOpen}
        title="Visor de documento"
        subtitle={pdf.viewer.title}
        documentUrl={pdf.viewer.url}
        onClose={pdf.close}
        onDownload={pdf.downloadCurrent}
      />
    </ManagementCard>
  );
};